import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { salesApiService } from '../../api/salesApi';
import {
    PencilSquareIcon,
    TrashIcon,
    CheckIcon,
    XMarkIcon,
    PlusIcon,
    FunnelIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ArrowsUpDownIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

/** YYYY-MM-DD veya ISO string → dd.mm.yyyy */
export function formatDateDMY(val) {
    if (val == null || val === '') return '';
    const s = String(val).slice(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return String(val);
    return `${m[3]}.${m[2]}.${m[1]}`;
}

function toIsoDateString(val) {
    if (val == null || val === '') return '';
    const s = String(val).slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
}

/** Filtre ve sıralama için tutarlı tarih anahtarı (YYYY-MM-DD) */
function toSortableDate(val) {
    if (val == null || val === '') return '';
    const iso = toIsoDateString(val);
    if (iso) return iso;
    const text = String(val).trim();
    const dmy = text.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
    if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
    const parsed = new Date(val);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
    return '';
}

function compareDates(a, b, field, direction = 'asc') {
    const da = toSortableDate(a[field]) || '';
    const db = toSortableDate(b[field]) || '';
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    const cmp = da.localeCompare(db);
    return direction === 'asc' ? cmp : -cmp;
}

function inDateRange(value, from, to) {
    if (!from && !to) return true;
    const v = toSortableDate(value);
    if (!v) return false;
    if (from && v < from) return false;
    if (to && v > to) return false;
    return true;
}

// --- UI Components ---
const Card = ({ children, className = '' }) => (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
        {children}
    </div>
);

const Label = ({ children, className = '' }) => (
    <label className={`block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 ${className}`}>
        {children}
    </label>
);

const Input = ({ className = '', ...props }) => (
    <input
        className={`w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white ${className}`}
        {...props}
    />
);

const Button = ({ children, className = '', variant = 'primary', ...props }) => {
    const baseStyle = 'inline-flex items-center justify-center rounded-md text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';
    const variants = {
        primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
        outline: 'border border-gray-300 bg-transparent hover:bg-gray-50 text-gray-700 dark:text-gray-300 dark:hover:bg-gray-800',
        ghost: 'bg-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
    };
    return (
        <button type="button" className={`${baseStyle} ${variants[variant] || variants.primary} ${className}`} {...props}>
            {children}
        </button>
    );
};

// --- DatePicker Component ---
const DatePicker = ({ value, onChange, placeholder, name }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(value || '');
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const generateCalendar = () => {
        const firstDay = new Date(currentYear, currentMonth, 1);
        const lastDay = new Date(currentYear, currentMonth + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();
        const adjustedStartDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

        const days = [];
        for (let i = 0; i < adjustedStartDay; i++) days.push(null);
        for (let day = 1; day <= daysInMonth; day++) days.push(day);
        return days;
    };

    const handleDateSelect = (day) => {
        if (day) {
            const formattedDate = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            setSelectedDate(formattedDate);
            onChange({ target: { name, value: formattedDate } });
            setIsOpen(false);
        }
    };

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    useEffect(() => { setSelectedDate(value || ''); }, [value]);

    const displayText = selectedDate ? formatDateDMY(selectedDate) : '';

    return (
        <div className="relative" ref={containerRef}>
            <Input
                type="text"
                value={displayText}
                readOnly
                placeholder={placeholder}
                onClick={() => setIsOpen(!isOpen)}
                className="cursor-pointer"
            />
            {isOpen && (
                <div className="absolute z-50 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 w-64">
                    <div className="flex items-center justify-between mb-2">
                        <button type="button" onClick={() => setCurrentMonth((prev) => {
                            if (prev === 0) { setCurrentYear((y) => y - 1); return 11; }
                            return prev - 1;
                        })} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">←</button>
                        <span className="text-sm font-medium">{monthNames[currentMonth]} {currentYear}</span>
                        <button type="button" onClick={() => setCurrentMonth((prev) => {
                            if (prev === 11) { setCurrentYear((y) => y + 1); return 0; }
                            return prev + 1;
                        })} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">→</button>
                    </div>
                    <div className="grid grid-cols-7 gap-0.5 mb-1 text-center text-xs text-gray-500">
                        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(d => <div key={d}>{d}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-0.5">
                        {generateCalendar().map((day, idx) => (
                            <button
                                type="button"
                                key={idx}
                                onClick={() => handleDateSelect(day)}
                                disabled={!day}
                                className={`p-0.5 text-xs rounded ${!day ? 'invisible' : 'hover:bg-blue-100 dark:hover:bg-blue-900'} ${day && selectedDate === `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` ? 'bg-blue-600 text-white' : ''}`}
                            >
                                {day}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const initialFilters = () => ({
    representative: '',
    country: '',
    city: '',
    visit_purpose: '',
    d_date_from: '',
    d_date_to: '',
    r_date_from: '',
    r_date_to: '',
    stay_hotel: '',
    flight_tickets: '',
    expenses: '',
    visa: '',
    akka_events: '',
    pax: ''
});

// --- Main Component ---
const TravelExpenses = () => {
    const [expenses, setExpenses] = useState([]);
    const [representatives, setRepresentatives] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [editFormData, setEditFormData] = useState({});

    const [filters, setFilters] = useState(initialFilters);
    const [isFiltersOpen, setIsFiltersOpen] = useState(true);
    const [sortBy, setSortBy] = useState('d_date');
    const [sortDir, setSortDir] = useState('asc');

    useEffect(() => {
        fetchData();
        fetchRepresentatives();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await salesApiService.getTravelExpenses();
            const listData = response.results || response.data || response;
            if (Array.isArray(listData)) setExpenses(listData);
            else setExpenses([]);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchRepresentatives = async () => {
        try {
            const response = await salesApiService.getRepresentatives();
            const listData = response.results || response.data || response;
            if (Array.isArray(listData)) setRepresentatives(listData);
        } catch (error) { /* ignore */ }
    };

    const filteredExpenses = useMemo(() => {
        const filtered = expenses.filter((item) => {
            if (filters.representative) {
                const val = filters.representative.toLowerCase();
                const name = item.representative_name?.toLowerCase() || '';
                if (!name.includes(val)) return false;
            }
            for (const key of ['country', 'city', 'visit_purpose']) {
                if (filters[key] && !item[key]?.toString().toLowerCase().includes(filters[key].toLowerCase())) return false;
            }
            if (!inDateRange(item.d_date, filters.d_date_from, filters.d_date_to)) return false;
            if (!inDateRange(item.r_date, filters.r_date_from, filters.r_date_to)) return false;
            for (const key of ['stay_hotel', 'flight_tickets', 'expenses', 'visa', 'akka_events', 'pax']) {
                if (filters[key] && !item[key]?.toString().startsWith(filters[key])) return false;
            }
            return true;
        });

        return [...filtered].sort((a, b) => compareDates(a, b, sortBy, sortDir));
    }, [expenses, filters, sortBy, sortDir]);

    const toggleDateSort = (field) => {
        if (sortBy === field) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(field);
            setSortDir('asc');
        }
    };

    const SortIcon = ({ field }) => {
        const active = sortBy === field;
        if (active) {
            return sortDir === 'asc'
                ? <ChevronUpIcon className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                : <ChevronDownIcon className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />;
        }
        return <ArrowsUpDownIcon className="w-3.5 h-3.5 text-gray-400" />;
    };

    const totals = filteredExpenses.reduce((acc, curr) => ({
        stay_hotel: acc.stay_hotel + (curr.stay_hotel || 0),
        flight_tickets: acc.flight_tickets + (curr.flight_tickets || 0),
        expenses: acc.expenses + (curr.expenses || 0),
        visa: acc.visa + (curr.visa || 0),
        akka_events: acc.akka_events + (curr.akka_events || 0),
        pax: acc.pax + (curr.pax || 0),
    }), { stay_hotel: 0, flight_tickets: 0, expenses: 0, visa: 0, akka_events: 0, pax: 0 });

    const handleExportExcel = () => {
        try {
            const rows = filteredExpenses.map((e) => ({
                Representative: e.representative_name || '',
                Country: e.country || '',
                City: e.city || '',
                'Visit purpose': e.visit_purpose || '',
                'Dep. date': formatDateDMY(e.d_date),
                'Arr. date': formatDateDMY(e.r_date),
                'Stay (hotel)': e.stay_hotel ?? '',
                'Flight tickets': e.flight_tickets ?? '',
                Expenses: e.expenses ?? '',
                Visa: e.visa ?? '',
                Other: e.akka_events ?? '',
                Pax: e.pax ?? ''
            }));
            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Travel Expenses');
            const stamp = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `travel_expenses_${stamp}.xlsx`);
        } catch (e) {
            console.error(e);
            alert('Excel oluşturulamadı.');
        }
    };

    const handleCreateNew = () => {
        if (editingId) return alert('Önce işlemi tamamlayın.');
        setEditingId('new');
        setEditFormData({
            representative: '', country: '', city: '', visit_purpose: '',
            d_date: '', r_date: '',
            stay_hotel: 0, flight_tickets: 0, expenses: 0, visa: 0, akka_events: 0, pax: 0
        });
    };

    const handleEditClick = (expense) => {
        if (editingId && editingId !== expense.id) return alert('Önce işlemi tamamlayın.');
        setEditingId(expense.id);
        setEditFormData({ ...expense });
    };

    const handleCancel = () => {
        setEditingId(null);
        setEditFormData({});
    };

    const handleSave = async () => {
        if (!editFormData.representative) return alert('Representative seçiniz.');
        try {
            setLoading(true);
            const apiCall = editingId === 'new'
                ? salesApiService.createTravelExpense(editFormData)
                : salesApiService.updateTravelExpense(editingId, editFormData);
            const response = await apiCall;
            if (response && (response.id || response.success)) {
                setEditingId(null);
                setEditFormData({});
                fetchData();
            } else alert('Kaydetme başarısız.');
        } catch (e) { alert(e.message); }
        finally { setLoading(false); }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Silinsin mi?')) {
            setLoading(true);
            await salesApiService.deleteTravelExpense(id);
            fetchData();
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setEditFormData((prev) => ({
            ...prev,
            [name]: type === 'number' ? (value === '' ? 0 : parseFloat(value)) : value
        }));
    };

    const handleFilterChange = (name, value) => {
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters(initialFilters());
    };

    const toDatePickerValue = (v) => (v == null ? '' : toIsoDateString(v) || String(v).slice(0, 10));

    return (
        <div className="space-y-6 p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Travel Expenses</h1>
                    <p className="text-sm text-gray-500">Sales Representatives harcamaları.</p>
                </div>
                <Button variant="outline" onClick={handleExportExcel} className="inline-flex items-center gap-2 px-3 py-2">
                    <ArrowDownTrayIcon className="w-4 h-4" />
                    Excel indir
                </Button>
            </div>

            <div className="flex items-center justify-start">
                <Button
                    onClick={handleCreateNew}
                    disabled={!!editingId}
                    variant="ghost"
                    className="text-gray-500 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 disabled:text-gray-300 dark:disabled:text-gray-600 p-2"
                    title="Yeni Kayıt"
                >
                    <PlusIcon className="w-4 h-4" />
                </Button>
            </div>

            <Card className="p-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                        <FunnelIcon className="w-5 h-5 text-gray-500" />
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Filtreler</h3>
                    </div>
                    {isFiltersOpen && (
                        <div className="flex items-center space-x-2">
                            <Button variant="outline" onClick={clearFilters} className="px-3 py-1">Temizle</Button>
                        </div>
                    )}
                </div>

                {isFiltersOpen && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                        <div><Label>Representative</Label><Input name="representative" value={filters.representative} onChange={(e) => handleFilterChange('representative', e.target.value)} placeholder="İsim ara..." /></div>
                        <div><Label>Country</Label><Input name="country" value={filters.country} onChange={(e) => handleFilterChange('country', e.target.value)} /></div>
                        <div><Label>City</Label><Input name="city" value={filters.city} onChange={(e) => handleFilterChange('city', e.target.value)} /></div>
                        <div><Label>Visit Purpose</Label><Input name="visit_purpose" value={filters.visit_purpose} onChange={(e) => handleFilterChange('visit_purpose', e.target.value)} /></div>
                        <div><Label>Dep. date — from</Label><DatePicker name="d_date_from" value={filters.d_date_from} onChange={(e) => handleFilterChange('d_date_from', e.target.value)} /></div>
                        <div><Label>Dep. date — to</Label><DatePicker name="d_date_to" value={filters.d_date_to} onChange={(e) => handleFilterChange('d_date_to', e.target.value)} /></div>
                        <div><Label>Arr. date — from</Label><DatePicker name="r_date_from" value={filters.r_date_from} onChange={(e) => handleFilterChange('r_date_from', e.target.value)} /></div>
                        <div><Label>Arr. date — to</Label><DatePicker name="r_date_to" value={filters.r_date_to} onChange={(e) => handleFilterChange('r_date_to', e.target.value)} /></div>
                    </div>
                )}
                <div className="flex justify-end mt-2">
                    <button type="button" onClick={() => setIsFiltersOpen(!isFiltersOpen)} className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700">
                        <span>{isFiltersOpen ? 'Gizle' : 'Filtreleri Göster'}</span>
                        <ChevronDownIcon className={`w-3 h-3 transition-transform ${isFiltersOpen ? 'rotate-180' : ''}`} />
                    </button>
                </div>
            </Card>

            <Card>
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <h3 className="text-sm font-medium">Kayıt Listesi</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left text-gray-600 dark:text-gray-300">
                        <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-700 uppercase">
                            <tr>
                                <th className="px-3 py-3 border-b">Representative</th>
                                <th className="px-3 py-3 border-b">Country</th>
                                <th className="px-3 py-3 border-b">City</th>
                                <th className="px-3 py-3 border-b">Purpose</th>
                                <th className="px-3 py-3 border-b">
                                    <button
                                        type="button"
                                        onClick={() => toggleDateSort('d_date')}
                                        className={`inline-flex items-center gap-1 uppercase cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 ${sortBy === 'd_date' ? 'text-blue-600 dark:text-blue-400 font-semibold' : ''}`}
                                        title="Departure tarihine göre sırala"
                                    >
                                        Dep. Date
                                        <SortIcon field="d_date" />
                                    </button>
                                </th>
                                <th className="px-3 py-3 border-b">
                                    <button
                                        type="button"
                                        onClick={() => toggleDateSort('r_date')}
                                        className={`inline-flex items-center gap-1 uppercase cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 ${sortBy === 'r_date' ? 'text-blue-600 dark:text-blue-400 font-semibold' : ''}`}
                                        title="Arrival tarihine göre sırala"
                                    >
                                        Arr. Date
                                        <SortIcon field="r_date" />
                                    </button>
                                </th>
                                <th className="px-3 py-3 border-b text-right">Stay</th>
                                <th className="px-3 py-3 border-b text-right">Flight</th>
                                <th className="px-3 py-3 border-b text-right">Expenses</th>
                                <th className="px-3 py-3 border-b text-right">Visa</th>
                                <th className="px-3 py-3 border-b text-right">Other</th>
                                <th className="px-3 py-3 border-b text-center">Pax</th>
                                <th className="px-3 py-3 border-b text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {editingId === 'new' && (
                                <tr className="bg-blue-50 dark:bg-blue-900/20">
                                    <td className="p-2"><select name="representative" value={editFormData.representative} onChange={handleChange} className="w-full text-xs border rounded p-1 dark:bg-gray-800 dark:border-gray-600"><option value="">Seçiniz</option>{representatives.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</select></td>
                                    <td className="p-2"><Input name="country" value={editFormData.country || ''} onChange={handleChange} /></td>
                                    <td className="p-2"><Input name="city" value={editFormData.city || ''} onChange={handleChange} /></td>
                                    <td className="p-2"><Input name="visit_purpose" value={editFormData.visit_purpose || ''} onChange={handleChange} /></td>
                                    <td className="p-2"><DatePicker name="d_date" value={toDatePickerValue(editFormData.d_date)} onChange={handleChange} /></td>
                                    <td className="p-2"><DatePicker name="r_date" value={toDatePickerValue(editFormData.r_date)} onChange={handleChange} /></td>
                                    <td className="p-2"><Input type="number" name="stay_hotel" value={editFormData.stay_hotel} onChange={handleChange} className="text-right" /></td>
                                    <td className="p-2"><Input type="number" name="flight_tickets" value={editFormData.flight_tickets} onChange={handleChange} className="text-right" /></td>
                                    <td className="p-2"><Input type="number" name="expenses" value={editFormData.expenses} onChange={handleChange} className="text-right" /></td>
                                    <td className="p-2"><Input type="number" name="visa" value={editFormData.visa} onChange={handleChange} className="text-right" /></td>
                                    <td className="p-2"><Input type="number" name="akka_events" value={editFormData.akka_events} onChange={handleChange} className="text-right" /></td>
                                    <td className="p-2"><Input type="number" name="pax" value={editFormData.pax} onChange={handleChange} className="text-center" /></td>
                                    <td className="p-2 text-center">
                                        <div className="flex justify-center space-x-1">
                                            <button type="button" onClick={handleSave} className="text-green-600"><CheckIcon className="w-5 h-5" /></button>
                                            <button type="button" onClick={handleCancel} className="text-red-600"><XMarkIcon className="w-5 h-5" /></button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {filteredExpenses.map((item) => {
                                const isEditing = editingId === item.id;
                                if (isEditing) {
                                    return (
                                        <tr key={item.id} className="bg-yellow-50 dark:bg-yellow-900/20">
                                            <td className="p-2"><select name="representative" value={editFormData.representative} onChange={handleChange} className="w-full text-xs border rounded p-1 dark:bg-gray-800 dark:border-gray-600"><option value="">Seçiniz</option>{representatives.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</select></td>
                                            <td className="p-2"><Input name="country" value={editFormData.country || ''} onChange={handleChange} /></td>
                                            <td className="p-2"><Input name="city" value={editFormData.city || ''} onChange={handleChange} /></td>
                                            <td className="p-2"><Input name="visit_purpose" value={editFormData.visit_purpose || ''} onChange={handleChange} /></td>
                                            <td className="p-2"><DatePicker name="d_date" value={toDatePickerValue(editFormData.d_date)} onChange={handleChange} /></td>
                                            <td className="p-2"><DatePicker name="r_date" value={toDatePickerValue(editFormData.r_date)} onChange={handleChange} /></td>
                                            <td className="p-2"><Input type="number" name="stay_hotel" value={editFormData.stay_hotel} onChange={handleChange} className="text-right" /></td>
                                            <td className="p-2"><Input type="number" name="flight_tickets" value={editFormData.flight_tickets} onChange={handleChange} className="text-right" /></td>
                                            <td className="p-2"><Input type="number" name="expenses" value={editFormData.expenses} onChange={handleChange} className="text-right" /></td>
                                            <td className="p-2"><Input type="number" name="visa" value={editFormData.visa} onChange={handleChange} className="text-right" /></td>
                                            <td className="p-2"><Input type="number" name="akka_events" value={editFormData.akka_events} onChange={handleChange} className="text-right" /></td>
                                            <td className="p-2"><Input type="number" name="pax" value={editFormData.pax} onChange={handleChange} className="text-center" /></td>
                                            <td className="p-2 text-center">
                                                <div className="flex justify-center space-x-1">
                                                    <button type="button" onClick={handleSave} className="text-green-600"><CheckIcon className="w-5 h-5" /></button>
                                                    <button type="button" onClick={handleCancel} className="text-gray-600"><XMarkIcon className="w-5 h-5" /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }
                                return (
                                    <tr key={item.id} onDoubleClick={() => handleEditClick(item)} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer">
                                        <td className="px-3 py-2 border-b font-medium">{item.representative_name}</td>
                                        <td className="px-3 py-2 border-b">{item.country}</td>
                                        <td className="px-3 py-2 border-b">{item.city}</td>
                                        <td className="px-3 py-2 border-b">{item.visit_purpose}</td>
                                        <td className="px-3 py-2 border-b whitespace-nowrap">{formatDateDMY(item.d_date)}</td>
                                        <td className="px-3 py-2 border-b whitespace-nowrap">{formatDateDMY(item.r_date)}</td>
                                        <td className="px-3 py-2 border-b text-right">{item.stay_hotel}</td>
                                        <td className="px-3 py-2 border-b text-right">{item.flight_tickets}</td>
                                        <td className="px-3 py-2 border-b text-right font-semibold">{item.expenses}</td>
                                        <td className="px-3 py-2 border-b text-right">{item.visa}</td>
                                        <td className="px-3 py-2 border-b text-right">{item.akka_events}</td>
                                        <td className="px-3 py-2 border-b text-center">{item.pax}</td>
                                        <td className="px-3 py-2 border-b text-center">
                                            <div className="flex justify-center space-x-2">
                                                <button type="button" onClick={(e) => { e.stopPropagation(); handleEditClick(item); }} className="text-blue-600"><PencilSquareIcon className="w-4 h-4" /></button>
                                                <button type="button" onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} className="text-red-600"><TrashIcon className="w-4 h-4" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredExpenses.length > 0 && (
                                <tr className="bg-gray-100 dark:bg-gray-800 font-bold">
                                    <td colSpan={6} className="px-3 py-2 text-right">TOPLAM:</td>
                                    <td className="px-3 py-2 text-right">{totals.stay_hotel}</td>
                                    <td className="px-3 py-2 text-right">{totals.flight_tickets}</td>
                                    <td className="px-3 py-2 text-right">{totals.expenses}</td>
                                    <td className="px-3 py-2 text-right">{totals.visa}</td>
                                    <td className="px-3 py-2 text-right">{totals.akka_events}</td>
                                    <td className="px-3 py-2 text-center">{totals.pax}</td>
                                    <td />
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>
            {loading && <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 text-white">Yükleniyor...</div>}
        </div>
    );
};
export default TravelExpenses;
